import { Signal } from '@angular/core';
import { PathKind, SchemaPath, SchemaPathRules, validate } from '@angular/forms/signals';
import { spanEpoch } from './chronologically-ordered.rule';

/** Milliseconds in an hour. */
const MS_PER_HOUR = 3_600_000;

/** Options of the {@link notTooFarInFuture} rule. */
export interface NotTooFarInFutureConfig {
    /** builds the message from the limit the value may not fall after */
    message?: (limit: string) => string;
}

/**
 * Signal-forms schema rule rejecting an instant that lies more than a configured number of hours
 * ahead of the moment it is checked. The timesheet page uses it to keep `endOfWork` from being
 * typed arbitrarily far into the future, with the limit read from the `EowFlexibleHours` config.
 *
 * The limit is computed from `Date.now()` inside the validator itself rather than through a
 * `limit: Signal<string>` handed in already resolved, unlike {@link notEarlierThan}: a limit
 * memoized in an outer `computed()` would only refresh when `maxHoursAhead` changes, so "now"
 * would go stale for as long as the page stays open with the config untouched. Reading the clock
 * fresh on every run — which still only happens when `context.value()` or `maxHoursAhead()`
 * changes, not on a timer — matches how the rest of this schema revalidates: on interaction, not
 * on wall time passing by itself.
 *
 * An unset or unparsable value is passed over: whether an empty value is acceptable is a
 * `required` question, not this one's.
 *
 * @param path the string path to validate
 * @param maxHoursAhead how far into the future the value may reach, as a signal
 * @param config optional message factory
 */
export function notTooFarInFuture<TPathKind extends PathKind = PathKind.Root>(
    path: SchemaPath<string, SchemaPathRules.Supported, TPathKind>,
    maxHoursAhead: Signal<number>,
    config?: NotTooFarInFutureConfig,
): void {
    validate(path, (context) => {
        const valueEpoch = spanEpoch(context.value());
        if (valueEpoch === undefined) {
            return undefined;
        }

        const limitEpoch = Date.now() + maxHoursAhead() * MS_PER_HOUR;
        if (valueEpoch <= limitEpoch) {
            return undefined;
        }

        const limit = new Date(limitEpoch).toISOString();
        return { kind: 'tooFarInFuture', message: config?.message?.(limit) ?? '' };
    });
}
